<template>
  <div class="travel-market">
    <section class="travel-hero">
      <div class="hero-inner">
        <div class="hero-grid">
          <div class="hero-copy-block">
            <div class="eyebrow">暑期早鸟 · 精选路线</div>
            <h1>
              <span>把好线路，</span>
              <span>摆到你面前</span>
            </h1>
            <p class="hero-copy">
              精选国内外度假、亲子、跟团、私家团与自由行产品，按预算、天数、主题和出发地快速找到合适行程。
            </p>
            <div class="hero-stats">
              <div class="stat">
                <strong>{{ total || routes.length }}</strong>
                <span>在售线路</span>
              </div>
              <div class="stat">
                <strong>{{ averageRating }}</strong>
                <span>综合评分</span>
              </div>
              <div class="stat">
                <strong>24h</strong>
                <span>专属顾问响应</span>
              </div>
            </div>
          </div>

          <form class="search-panel" @submit.prevent="handleSearch">
            <div class="search-title">
              <span>查找旅行线路</span>
              <span class="badge">按好评排序</span>
            </div>
            <div class="field">
              <label for="routeName">线路名称 / 关键词</label>
              <input
                id="routeName"
                v-model.trim="filters.routeName"
                type="search"
                placeholder="欧洲、云南、亲子海岛"
              />
            </div>
            <div class="field-row">
              <div class="field">
                <label for="departurePlace">出发地</label>
                <input
                  id="departurePlace"
                  v-model.trim="filters.departurePlace"
                  type="text"
                  placeholder="深圳"
                />
              </div>
              <div class="field">
                <label for="destination">目的地</label>
                <input
                  id="destination"
                  v-model.trim="filters.destination"
                  type="text"
                  placeholder="威尼斯"
                />
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <label for="travelDays">行程天数</label>
                <input
                  id="travelDays"
                  v-model.trim="filters.travelDays"
                  type="text"
                  placeholder="7天6夜"
                />
              </div>
              <div class="field">
                <label for="travelSubject">旅行主题</label>
                <select id="travelSubject" v-model="filters.travelSubject">
                  <option value="">全部主题</option>
                  <option v-for="item in subjectOptions" :key="item" :value="item">
                    {{ item }}
                  </option>
                </select>
              </div>
            </div>
            <button class="primary-btn" type="submit">搜索线路</button>
            <button class="ghost-btn" type="button" @click="resetFilters">
              重置条件
            </button>
          </form>
        </div>
      </div>
    </section>

    <main class="main">
      <section class="quick-strip" aria-label="主题入口">
        <button
          v-for="item in themeCards"
          :key="item.title"
          class="quick-card"
          type="button"
          @click="selectSubject(item.title)"
        >
          <span class="quick-icon">{{ item.icon }}</span>
          <span>
            <strong>{{ item.title }}</strong>
            <span>{{ item.desc }}</span>
          </span>
        </button>
      </section>

      <section id="routes" class="routes-section">
        <div class="section-head">
          <div>
            <h2>热卖线路</h2>
            <p>
              按真实成交、近期评价和线路评分排序，适合直接选购或加入对比。
            </p>
          </div>
          <div class="tabs" role="tablist" aria-label="线路分类">
            <button
              v-for="tab in routeTabs"
              :key="tab.label"
              :class="['tab', { active: filters.routeType === tab.value }]"
              type="button"
              @click="selectType(tab.value)"
            >
              {{ tab.label }}
            </button>
          </div>
        </div>

        <div v-if="loading" class="state-panel">线路加载中...</div>
        <div v-else-if="error" class="state-panel error">
          {{ error }}
          <button class="mini-btn" type="button" @click="fetchRoutes">重试</button>
        </div>
        <div v-else-if="routes.length === 0" class="state-panel">
          暂无匹配线路，换个目的地或主题再试试。
        </div>

        <div v-else class="route-grid" aria-label="线路商品列表">
          <article v-for="item in routes" :key="item.id" class="route-card">
            <div class="route-media">
              <img
                :src="getImage(item)"
                :alt="item.routeName || '旅行线路图片'"
                @error="handleImageError"
              />
              <span class="route-tag">{{ item.travelSubject || routeTypeText(item.routeType) }}</span>
            </div>
            <div class="route-body">
              <div class="route-meta">
                <span>{{ item.destination || "目的地待定" }} · {{ item.travelDays || "行程待定" }}</span>
                <span>{{ formatRating(item.rating) }}</span>
              </div>
              <h3>{{ item.routeName || "精选旅行线路" }}</h3>
              <p class="route-feature">{{ item.routeFeature || "专业团队打磨行程细节，出行节奏舒适清晰。" }}</p>
              <div class="route-points">
                <span class="pill">{{ routeTypeText(item.routeType) }}</span>
                <span v-if="item.departurePlace" class="pill">{{ item.departurePlace }}出发</span>
                <span v-if="item.travelSubject" class="pill">{{ item.travelSubject }}</span>
              </div>
              <div class="route-foot">
                <div class="price">
                  {{ formatPrice(item.price) }} <small>起 / 人</small>
                </div>
                <button class="mini-btn" type="button" @click="openDetail(item)">
                  查看
                </button>
              </div>
            </div>
          </article>
        </div>

        <div class="pagination" v-if="total > filters.pageSize">
          <button class="ghost-btn" type="button" :disabled="filters.pageNum === 1" @click="changePage(-1)">
            上一页
          </button>
          <span>第 {{ filters.pageNum }} / {{ totalPages }} 页</span>
          <button class="ghost-btn" type="button" :disabled="filters.pageNum >= totalPages" @click="changePage(1)">
            下一页
          </button>
        </div>
      </section>

      <section class="feature-band">
        <div class="feature-grid">
          <div class="feature-photo" role="img" aria-label="公路旅行风景"></div>
          <div class="feature-copy">
            <h2>下单前后，都有明确服务链路</h2>
            <p>
              从产品核验、名额确认、合同签署到出行提醒，线路商品和旅行服务拆成清晰节点，降低售前沟通成本。
            </p>
            <div class="steps">
              <div v-for="(step, index) in steps" :key="step.title" class="step">
                <span class="step-num">{{ String(index + 1).padStart(2, "0") }}</span>
                <span>
                  <strong>{{ step.title }}</strong>
                  <span>{{ step.desc }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="footer-cta">
        <div class="cta-inner">
          <div>
            <h2>把出行需求交给顾问</h2>
            <p>
              预算、人数、日期、酒店偏好和不想踩的坑都可以先填好，顾问会从在售线路里给出匹配方案。
            </p>
          </div>
          <div class="cta-actions">
            <a class="phone-btn" href="tel:4008053118">
              联系顾问 400-805-3118
            </a>
          </div>
        </div>
      </section>
    </main>

    <div v-if="activeRoute" class="route-modal" @click.self="closeDetail">
      <article class="modal-panel">
        <button class="modal-close" type="button" aria-label="关闭" @click="closeDetail">×</button>
        <img
          class="modal-cover"
          :src="getImage(activeRoute)"
          :alt="activeRoute.routeName || '线路详情图片'"
          @error="handleImageError"
        />
        <div class="modal-body">
          <div class="route-meta">
            <span>{{ activeRoute.destination }} · {{ activeRoute.travelDays }}</span>
            <span>{{ formatRating(activeRoute.rating) }}</span>
          </div>
          <h2>{{ activeRoute.routeName }}</h2>
          <div class="modal-info">
            <span>出发地：{{ activeRoute.departurePlace || "待定" }}</span>
            <span>主题：{{ activeRoute.travelSubject || "精选线路" }}</span>
            <span>价格：{{ formatPrice(activeRoute.price) }} 起 / 人</span>
          </div>
          <div v-if="activeRoute.detail" class="detail-content" v-html="activeRoute.detail"></div>
          <p v-else class="detail-empty">该线路暂无详情介绍，请联系顾问获取完整方案。</p>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import { queryTravelRoutes } from "@/apis/travel";

const fallbackImages = [
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=82",
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=82",
  "https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=1200&q=82",
];

export default {
  name: "TravelRoutes",
  data() {
    return {
      loading: false,
      error: "",
      routes: [],
      total: 0,
      activeRoute: null,
      filters: {
        pageNum: 1,
        pageSize: 6,
        routeName: "",
        routeType: "",
        destination: "",
        departurePlace: "",
        travelDays: "",
        travelSubject: "",
      },
      routeTabs: [
        { label: "全部", value: "" },
        { label: "国内游", value: "domestic" },
        { label: "境外游", value: "overseas" },
      ],
      subjectOptions: ["海岛度假", "山野漫步", "城市漫游", "亲子精选", "私家团"],
      themeCards: [
        { icon: "岛", title: "海岛度假", desc: "巴厘岛、普吉、马尔代夫" },
        { icon: "山", title: "山野漫步", desc: "雨崩、川西、阿尔卑斯" },
        { icon: "城", title: "城市漫游", desc: "东京、巴黎、威尼斯" },
        { icon: "亲", title: "亲子精选", desc: "乐园、研学、轻松酒店" },
      ],
      steps: [
        { title: "供应商准入", desc: "资质、保险、履约记录与真实评价共同影响线路推荐。" },
        { title: "透明报价", desc: "必付费用、可选升级、儿童价和单房差分开展示。" },
        { title: "行程管家", desc: "出发前资料提醒，旅途中异常协同，返程后评价追踪。" },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.filters.pageSize));
    },
    averageRating() {
      if (!this.routes.length) return "4.9";
      const sum = this.routes.reduce((count, item) => count + Number(item.rating || 0), 0);
      return (sum / this.routes.length).toFixed(1);
    },
  },
  created() {
    this.fetchRoutes();
  },
  methods: {
    async fetchRoutes() {
      this.loading = true;
      this.error = "";
      try {
        const params = this.cleanParams(this.filters);
        const res = await queryTravelRoutes(params);
        const data = res.data || {};
        this.routes = data.rows || [];
        this.total = data.total || this.routes.length;
      } catch (err) {
        this.error = "线路数据暂时无法加载，请稍后再试。";
      } finally {
        this.loading = false;
      }
    },
    cleanParams(params) {
      const next = {};
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          next[key] = params[key];
        }
      });
      return next;
    },
    handleSearch() {
      this.filters.pageNum = 1;
      this.fetchRoutes();
    },
    resetFilters() {
      this.filters = {
        pageNum: 1,
        pageSize: 6,
        routeName: "",
        routeType: "",
        destination: "",
        departurePlace: "",
        travelDays: "",
        travelSubject: "",
      };
      this.fetchRoutes();
    },
    selectType(type) {
      this.filters.routeType = type;
      this.handleSearch();
    },
    selectSubject(subject) {
      this.filters.travelSubject = subject;
      this.handleSearch();
      this.$nextTick(this.scrollToRoutes);
    },
    changePage(step) {
      const nextPage = this.filters.pageNum + step;
      if (nextPage < 1 || nextPage > this.totalPages) return;
      this.filters.pageNum = nextPage;
      this.fetchRoutes();
      this.$nextTick(this.scrollToRoutes);
    },
    getImage(item) {
      if (item.mainImages) {
        return String(item.mainImages).split(",")[0];
      }
      return fallbackImages[Number(item.id || 0) % fallbackImages.length];
    },
    handleImageError(event) {
      event.target.src = fallbackImages[0];
    },
    routeTypeText(type) {
      if (type === "domestic") return "国内游";
      if (type === "overseas") return "境外游";
      return "精选线路";
    },
    formatRating(rating) {
      if (!rating && rating !== 0) return "暂无评分";
      return `${Number(rating).toFixed(1)} 分`;
    },
    formatPrice(price) {
      if (!price && price !== 0) return "价格咨询";
      return `￥${Number(price).toLocaleString("zh-CN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
    openDetail(route) {
      this.activeRoute = route;
      document.body.style.overflow = "hidden";
    },
    closeDetail() {
      this.activeRoute = null;
      document.body.style.overflow = "";
    },
    scrollToRoutes() {
      const target = document.getElementById("routes");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    scrollToSearch() {
      const target = document.querySelector(".search-panel");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.travel-market {
  --ink: #16202f;
  --muted: #667084;
  --line: #dfe5ec;
  --paper: #ffffff;
  --wash: #f5f8fb;
  --blue: #1768d1;
  --blue-dark: #0f4f9e;
  --green: #0f8f73;
  --gold: #c67a12;
  --coral: #d94f3d;
  --shadow: 0 18px 45px rgba(25, 42, 70, 0.12);
  --radius: 8px;
  min-height: 100vh;
  color: var(--ink);
  background: var(--wash);
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC",
    "Microsoft YaHei", sans-serif;
}

.travel-market * {
  box-sizing: border-box;
  letter-spacing: 0;
}

.travel-hero {
  min-height: clamp(560px, 82vh, 760px);
  display: flex;
  align-items: flex-end;
  background-image: linear-gradient(180deg, rgba(8, 21, 39, 0.1), rgba(8, 21, 39, 0.82)),
    url("https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=2200&q=82");
  background-size: cover;
  background-position: center;
  color: #fff;
}

.hero-inner,
.main {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
}

.hero-inner {
  padding: 108px 0 34px;
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 28px;
  align-items: flex-end;
}

.eyebrow,
.badge {
  width: fit-content;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
}

.eyebrow {
  border: 1px solid rgba(255, 255, 255, 0.45);
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.13);
  color: rgba(255, 255, 255, 0.94);
}

.hero-copy-block h1 {
  max-width: 760px;
  margin: 18px 0 14px;
  font-size: clamp(42px, 7vw, 82px);
  line-height: 0.98;
}

.hero-copy-block h1 span {
  display: block;
}

.hero-copy {
  max-width: 640px;
  margin: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: clamp(16px, 2.2vw, 20px);
  line-height: 1.7;
}

.hero-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 28px;
  max-width: 680px;
}

.stat {
  border-left: 1px solid rgba(255, 255, 255, 0.38);
  padding: 3px 0 3px 15px;
}

.stat strong {
  display: block;
  font-size: 25px;
  line-height: 1.2;
}

.stat span {
  color: rgba(255, 255, 255, 0.76);
  font-size: 13px;
}

.search-panel {
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: var(--shadow);
  color: var(--ink);
  padding: 18px;
}

.search-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  font-weight: 800;
}

.badge {
  padding: 5px 9px;
  background: #e8f5f1;
  color: var(--green);
  font-size: 12px;
}

.field {
  display: grid;
  gap: 7px;
  margin-top: 12px;
}

.field label {
  color: #596579;
  font-size: 13px;
  font-weight: 700;
}

.field input,
.field select {
  width: 100%;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  min-height: 46px;
  padding: 0 12px;
  background: #fff;
  color: var(--ink);
  outline: none;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.primary-btn,
.ghost-btn {
  width: 100%;
  min-height: 46px;
  border-radius: var(--radius);
  cursor: pointer;
  font-weight: 800;
}

.primary-btn {
  margin-top: 16px;
  border: 0;
  background: var(--blue);
  color: #fff;
}

.ghost-btn {
  margin-top: 10px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--blue-dark);
}

.ghost-btn:disabled {
  color: #aab4c3;
  cursor: not-allowed;
}

.quick-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  transform: translateY(-24px);
  margin-bottom: 20px;
}

.quick-card {
  min-height: 92px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--paper);
  box-shadow: 0 12px 32px rgba(25, 42, 70, 0.08);
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 16px;
  color: var(--ink);
  text-align: left;
  cursor: pointer;
}

.quick-icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: #edf5ff;
  color: var(--blue);
  font-size: 18px;
  font-weight: 900;
}

.quick-card:nth-child(2) .quick-icon {
  background: #e8f5f1;
  color: var(--green);
}

.quick-card:nth-child(3) .quick-icon {
  background: #fff2df;
  color: var(--gold);
}

.quick-card:nth-child(4) .quick-icon {
  background: #fdecea;
  color: var(--coral);
}

.quick-card strong,
.quick-card span span {
  display: block;
}

.quick-card strong {
  margin-bottom: 4px;
  font-size: 16px;
}

.quick-card span span {
  color: var(--muted);
  font-size: 13px;
  line-height: 1.4;
}

.section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin: 34px 0 18px;
}

.section-head h2 {
  margin: 0 0 8px;
  font-size: clamp(26px, 4vw, 38px);
  line-height: 1.08;
}

.section-head p {
  max-width: 620px;
  margin: 0;
  color: var(--muted);
  line-height: 1.7;
}

.tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.tab {
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 10px 14px;
  background: #fff;
  color: #48556a;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.tab.active {
  border-color: var(--blue);
  background: var(--blue);
  color: #fff;
}

.state-panel {
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: #fff;
  padding: 32px;
  color: var(--muted);
  text-align: center;
}

.state-panel.error {
  color: var(--coral);
}

.route-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.route-card {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: #fff;
  box-shadow: 0 10px 28px rgba(25, 42, 70, 0.07);
}

.route-media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: #d9e3ef;
  overflow: hidden;
}

.route-media img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.28s ease;
}

.route-card:hover .route-media img {
  transform: scale(1.04);
}

.route-tag {
  position: absolute;
  left: 12px;
  top: 12px;
  border-radius: 999px;
  padding: 6px 9px;
  background: rgba(22, 32, 47, 0.74);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.route-body {
  padding: 14px;
}

.route-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--muted);
  font-size: 13px;
  min-height: 22px;
}

.route-body h3 {
  min-height: 52px;
  margin: 8px 0 10px;
  font-size: 18px;
  line-height: 1.45;
}

.route-feature {
  display: -webkit-box;
  min-height: 66px;
  margin: 0 0 12px;
  overflow: hidden;
  color: #536176;
  font-size: 13px;
  line-height: 1.7;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.route-points {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 34px;
  margin-bottom: 12px;
}

.pill {
  border-radius: 999px;
  padding: 6px 8px;
  background: #f0f4f8;
  color: #536176;
  font-size: 12px;
  font-weight: 700;
}

.route-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid var(--line);
  padding-top: 12px;
}

.price {
  color: var(--coral);
  font-weight: 900;
  font-size: 22px;
  white-space: nowrap;
}

.price small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.mini-btn {
  min-width: 82px;
  border: 1px solid var(--blue);
  border-radius: var(--radius);
  padding: 9px 10px;
  background: #fff;
  color: var(--blue-dark);
  font-weight: 800;
  cursor: pointer;
}

.pagination {
  display: grid;
  grid-template-columns: 120px 1fr 120px;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
  color: var(--muted);
  text-align: center;
}

.pagination .ghost-btn {
  margin-top: 0;
}

.feature-band {
  margin: 52px calc(50% - 50vw) 0;
  padding: 54px max(16px, calc((100vw - 1180px) / 2));
  background: #102033;
  color: #fff;
}

.feature-grid {
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
  gap: 28px;
  align-items: center;
}

.feature-photo {
  min-height: 430px;
  border-radius: var(--radius);
  background-image: linear-gradient(180deg, rgba(16, 32, 51, 0.06), rgba(16, 32, 51, 0.2)),
    url("https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1400&q=82");
  background-size: cover;
  background-position: center;
}

.feature-copy h2 {
  margin: 0 0 14px;
  font-size: clamp(30px, 4.2vw, 48px);
  line-height: 1.08;
}

.feature-copy p {
  margin: 0 0 24px;
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.8;
}

.steps {
  display: grid;
  gap: 12px;
}

.step {
  display: grid;
  grid-template-columns: 40px 1fr;
  gap: 12px;
  align-items: start;
  border-bottom: 1px solid rgba(255, 255, 255, 0.13);
  padding-bottom: 14px;
}

.step-num {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.12);
  color: #8fe0ce;
  font-weight: 900;
}

.step strong {
  display: block;
  margin-bottom: 4px;
}

.step span span {
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.55;
  font-size: 14px;
}

.footer-cta {
  margin: 0 calc(50% - 50vw);
  padding: 36px max(16px, calc((100vw - 1180px) / 2));
  background: #fff;
  border-top: 1px solid var(--line);
}

.cta-inner {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 20px;
  align-items: center;
}

.cta-inner h2 {
  margin: 0 0 8px;
  font-size: clamp(24px, 3.2vw, 34px);
}

.cta-inner p {
  margin: 0;
  color: var(--muted);
  line-height: 1.7;
}

.phone-btn {
  width: 100%;
  min-height: 46px;
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--blue);
  color: #fff;
  font-weight: 800;
  text-decoration: none;
}

.route-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 28px;
  background: rgba(10, 20, 34, 0.68);
}

.modal-panel {
  position: relative;
  width: min(960px, 100%);
  max-height: min(86vh, 900px);
  overflow: auto;
  border-radius: var(--radius);
  background: #fff;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
}

.modal-close {
  position: sticky;
  top: 14px;
  float: right;
  z-index: 2;
  width: 38px;
  height: 38px;
  margin: 14px 14px 0 0;
  border: 0;
  border-radius: 50%;
  background: rgba(22, 32, 47, 0.78);
  color: #fff;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}

.modal-cover {
  width: 100%;
  height: clamp(220px, 34vw, 360px);
  display: block;
  object-fit: cover;
}

.modal-body {
  padding: 24px;
}

.modal-body h2 {
  margin: 10px 0 14px;
  font-size: clamp(24px, 3vw, 34px);
  line-height: 1.25;
}

.modal-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.modal-info span {
  border-radius: 999px;
  padding: 8px 10px;
  background: #f0f4f8;
  color: #536176;
  font-size: 13px;
  font-weight: 700;
}

.detail-content {
  color: #28364a;
  line-height: 1.8;
  word-break: break-word;
}

.detail-content >>> img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}

.detail-empty {
  color: var(--muted);
}

@media (max-width: 980px) {
  .hero-grid,
  .feature-grid,
  .cta-inner {
    grid-template-columns: 1fr;
  }

  .search-panel {
    max-width: 620px;
  }

  .quick-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .route-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .hero-inner,
  .main {
    width: min(100% - 24px, 1180px);
  }

  .travel-hero {
    min-height: auto;
  }

  .hero-inner {
    padding-top: 84px;
  }

  .hero-stats,
  .quick-strip,
  .route-grid,
  .field-row,
  .pagination {
    grid-template-columns: 1fr;
  }

  .section-head {
    display: grid;
    align-items: start;
  }

  .route-body h3,
  .route-feature,
  .route-points {
    min-height: auto;
  }

  .feature-photo {
    min-height: 300px;
  }

  .route-modal {
    padding: 12px;
  }

  .modal-body {
    padding: 18px;
  }
}
</style>
